import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { eventsAPI, driverAPI } from '../../services/api';
import './DashboardHome.css';

function DashboardHome() {
  const { user } = useAuth();
  const [todayEvents, setTodayEvents] = useState([]);
  const [activeDrivers, setActiveDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Phase 6: Real data from database - no more demo data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch today's events from real database
        const eventsResponse = await eventsAPI.getTodayEvents();
        if (eventsResponse.success) {
          setTodayEvents(eventsResponse.data.events || []);
        }

        // Fetch active drivers from real database
        const driversResponse = await driverAPI.getActiveDrivers();
        if (driversResponse.success) {
          setActiveDrivers(driversResponse.data.drivers || []);
        }

        setError(null);
      } catch (err) {
        console.error('Dashboard data fetch error:', err);
        setError('Fehler beim Laden der Dashboard-Daten');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Generate dynamic stats from real data
  const quickStats = [
    {
      icon: '🎉',
      title: 'Heutige Events',
      value: todayEvents.length.toString(),
      subtitle: todayEvents.slice(0, 3).map(e => e.location).join(', ') || 'Keine Events heute',
      color: 'blue'
    },
    {
      icon: '🚗',
      title: 'Online Fahrer',
      value: activeDrivers.filter(d => d.status === 'online').length.toString(),
      subtitle: 'in München aktiv',
      color: 'green'
    },
    {
      icon: '🌤️',
      title: 'Wetter München',
      value: '22°C',
      subtitle: 'Teilweise bewölkt',
      color: 'orange'
    },
    {
      icon: '💰',
      title: 'Potenzial',
      value: todayEvents.length > 2 ? 'Hoch' : todayEvents.length > 0 ? 'Mittel' : 'Niedrig',
      subtitle: 'Basierend auf Events',
      color: 'purple'
    }
  ];

  // Find best event recommendation based on real data
  const bestEvent = todayEvents.length > 0 
    ? todayEvents.reduce((prev, current) => 
        (prev.expected_visitors > current.expected_visitors) ? prev : current
      )
    : null;

  if (loading) {
    return (
      <div className="dashboard-home">
        <div className="dashboard-header">
          <h1>Lade Dashboard... 🚖</h1>
        </div>
        <div className="loading-spinner">Daten werden geladen...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-home">
        <div className="dashboard-header">
          <h1>Dashboard Fehler</h1>
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-home">
      <div className="dashboard-header">
        <h1>Willkommen zurück, {user?.firstName || user?.first_name}! 🚖</h1>
        <p>Hier ist dein aktueller Überblick für München heute</p>
      </div>

      <div className="stats-grid">
        {quickStats.map((stat, index) => (
          <div key={index} className={`stat-card ${stat.color}`}>
            <div className="stat-icon">{stat.icon}</div>
            <div className="stat-content">
              <h3 className="stat-value">{stat.value}</h3>
              <p className="stat-title">{stat.title}</p>
              <span className="stat-subtitle">{stat.subtitle}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="dashboard-sections">
        <div className="section events-section">
          <div className="section-header">
            <h2>🎉 Heutige Events in München</h2>
            <button className="section-action">Alle anzeigen</button>
          </div>
          
          <div className="events-list">
            {todayEvents.length > 0 ? (
              todayEvents.map((event) => (
                <div key={event.id} className="event-card">
                  <div className="event-info">
                    <h4 className="event-name">{event.name}</h4>
                    <div className="event-details">
                      <span className="event-time">⏰ {event.startTime || 'Zeit TBD'}</span>
                      <span className="event-location">📍 {event.location}</span>
                      <span className="event-visitors">👥 {event.expected_visitors?.toLocaleString() || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="event-actions">
                    <span className="event-distance">{event.distanceKm ? `${event.distanceKm} km` : 'Entfernung berechnen'}</span>
                    <button className="go-button">Hinfahren</button>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-events">
                <p>Heute sind keine Events in München geplant.</p>
              </div>
            )}
          </div>
        </div>

        <div className="section recommendations-section">
          <div className="section-header">
            <h2>💡 Empfehlungen</h2>
          </div>
          
          <div className="recommendations">
            {bestEvent && (
              <div className="recommendation-card best">
                <div className="recommendation-header">
                  <span className="recommendation-badge">🏆 Beste Wahl</span>
                </div>
                <h4>{bestEvent.name}</h4>
                <p>{bestEvent.expected_visitors?.toLocaleString()} erwartete Besucher - {bestEvent.demandLevel || 'Hohe'} Nachfrage nach Taxis</p>
                <div className="recommendation-stats">
                  <span>⭐ Empfohlen</span>
                  <span>💰 {bestEvent.estimatedTaxis ? `~${bestEvent.estimatedTaxis} Taxis benötigt` : 'Guter Verdienst'}</span>
                </div>
              </div>
            )}

            <div className="recommendation-card">
              <h4>🌧️ Wetter-Tipp München</h4>
              <p>Heute Abend könnte es regnen. Mehr Fahrgäste erwarten Taxis in der Innenstadt.</p>
            </div>

            <div className="recommendation-card">
              <h4>⏰ Rush Hour München</h4>
              <p>17:00-19:00 Uhr: Maximilianstraße und Marienplatz haben hohe Nachfrage</p>
            </div>
          </div>
        </div>
      </div>

      <div className="quick-actions">
        <button className="quick-action-btn primary">
          🟢 Online gehen
        </button>
        <button className="quick-action-btn secondary">
          🗺️ Zur Karte
        </button>
        <button className="quick-action-btn secondary">
          📊 Statistiken
        </button>
        <button className="quick-action-btn emergency">
          🚨 Notfall
        </button>
      </div>
    </div>
  );
}

export default DashboardHome;
